import { useCallback, useEffect, useState } from 'react';
import type { Diagram } from '../../store/types.ts';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3393/api';

type ShareModalProps = {
  open: boolean;
  diagram: Diagram | undefined;
  onClose: () => void;
};

function LinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M6.5 9.5a3.5 3.5 0 005 0l2-2a3.5 3.5 0 00-5-5l-1 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M9.5 6.5a3.5 3.5 0 00-5 0l-2 2a3.5 3.5 0 005 5l1-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M3 8.5l3.5 3.5 6.5-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ShareModal({ open, diagram, onClose }: ShareModalProps) {
  const [isShared, setIsShared] = useState(false);
  const [shareToken, setShareToken] = useState('');
  const [shareMode, setShareMode] = useState<'view' | 'edit'>('view');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset state when diagram changes
  useEffect(() => {
    setIsShared(false);
    setShareToken('');
    setShareMode('view');
    setCopied(false);
  }, [diagram?.id]);

  const getAccessToken = useCallback(() => {
    return localStorage.getItem('sf.auth.accessToken') || '';
  }, []);

  const shareUrl = shareToken
    ? `${window.location.origin}/shared/${shareToken}`
    : '';

  const handleEnable = useCallback(async (mode: 'view' | 'edit') => {
    if (!diagram) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/diagrams/${diagram.id}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({ mode }),
      });
      if (!res.ok) throw new Error('Failed to enable sharing');
      const body = await res.json();
      const data = body.data || body;
      setShareToken(data.shareToken);
      setIsShared(true);
      setShareMode(mode);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [diagram, getAccessToken]);

  const handleDisable = useCallback(async () => {
    if (!diagram) return;
    setLoading(true);
    try {
      await fetch(`${BASE_URL}/diagrams/${diagram.id}/share`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });
      setIsShared(false);
      setShareToken('');
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [diagram, getAccessToken]);

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [shareUrl]);

  const handleModeChange = useCallback(async (mode: 'view' | 'edit') => {
    setShareMode(mode);
    if (isShared) {
      await handleEnable(mode);
    }
  }, [isShared, handleEnable]);

  if (!open) return null;

  return (
    <div className="ds-modal-overlay" onClick={onClose}>
      <div className="ds-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
        <div className="ds-modal__head">
          <h2 className="ds-modal__title">Share diagram</h2>
          <button type="button" className="ds-icon-btn" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        {!diagram ? (
          <p style={{ fontSize: '13px', color: 'var(--ds-text-secondary)' }}>
            Select a diagram to share.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-4)' }}>
            {/* Toggle sharing */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>Public link</div>
                <div style={{ fontSize: '12px', color: 'var(--ds-text-tertiary)', marginTop: '2px' }}>
                  Anyone with the link can {shareMode === 'edit' ? 'edit' : 'view'}
                </div>
              </div>
              <button
                type="button"
                className={`ds-btn ds-btn--sm ${isShared ? 'ds-btn--danger' : 'ds-btn--primary'}`}
                onClick={isShared ? handleDisable : () => handleEnable(shareMode)}
                disabled={loading}
              >
                {loading ? '...' : isShared ? 'Disable' : 'Enable'}
              </button>
            </div>

            {/* Share mode toggle */}
            <div className="ds-field">
              <label className="ds-field__label">Access level</label>
              <div style={{ display: 'flex', gap: 'var(--ds-space-2)' }}>
                <button
                  type="button"
                  className={`ds-btn ds-btn--sm ${shareMode === 'view' ? 'ds-btn--primary' : ''}`}
                  onClick={() => handleModeChange('view')}
                  style={{ flex: 1 }}
                >
                  View only
                </button>
                <button
                  type="button"
                  className={`ds-btn ds-btn--sm ${shareMode === 'edit' ? 'ds-btn--primary' : ''}`}
                  onClick={() => handleModeChange('edit')}
                  style={{ flex: 1 }}
                >
                  Can edit
                </button>
              </div>
            </div>

            {/* Share URL */}
            {isShared && shareUrl && (
              <div style={{ display: 'flex', gap: 'var(--ds-space-2)', alignItems: 'center' }}>
                <input
                  readOnly
                  value={shareUrl}
                  className="ds-field__input"
                  style={{ flex: 1, fontSize: '12px' }}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button
                  type="button"
                  className="ds-btn ds-btn--primary ds-btn--sm"
                  onClick={handleCopy}
                  style={{ gap: '4px', minWidth: '80px' }}
                >
                  {copied ? <CheckIcon /> : <LinkIcon />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
